import type { Signal } from '@preact/signals';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { resolveLauncherStyle, resolvePhoneLauncher, type RequestType } from '@feedback/shared';
import type { WidgetConfig } from '../api/types';
import type { ScreenshotCapturer } from '../screenshot/capturer';
import type { ScreenRecorder } from '../recording/recorder';
import { Button } from './Button';
import { render as renderInto } from 'preact';
import { DOCK_ICON_MARKUP, DockIcon, EDGE_PANEL_ID, EdgeTab } from './EdgeTab';
import { resolveEdgeTabAppearance } from './edge-tab-appearance';
import { edgeTabStyles } from './edge-tab-styles';
import { loadBrandFonts } from './fonts';
import { clearHostToastLift, setHostToastLift } from './host-open-width';
import { Modal } from './Modal';
import type { FiledReport, HostConsent, ModalCapture, ModalSubmitPayload, ModalSubmitResult } from './Modal';
import { useDraggable } from './useDraggable';

export interface AppProps {
  /** Reactive open/close state owned by the controller; toggled by `open()`/`close()`. */
  isOpen: Signal<boolean>;
  /** Request type the modal should open with — owned by the controller's `open(type)`. */
  requestedType: Signal<RequestType>;
  onOpen: () => void;
  /** Closes the modal — wired to the controller's `close()`. */
  onClose: () => void;
  /** Host toast hook. The controller emits `fm:close` with this report. */
  onFiled?: (filed: FiledReport) => void;
  /**
   * `hide()` / `show()`. Optional so unit tests that render `App` directly
   * keep the tab on screen.
   */
  triggerHidden?: Signal<boolean>;
  /** `mountTrigger(el)`. While set, the edge tab is not rendered. */
  dockHost?: Signal<HTMLElement | null>;
  /**
   * Takes the capture as its THIRD argument, and typing it here is the point.
   *
   * A two-parameter function is assignable to a three-parameter type, so a
   * caller that forgot the capture type-checked cleanly and silently posted a
   * report with no screenshot. That is how this shipped to CI once already.
   */
  onSubmit: (
    payload: ModalSubmitPayload,
    attachments: File[],
    capture: ModalCapture | null,
    consents: Record<string, boolean>,
  ) => Promise<ModalSubmitResult>;
  /** Forwarded to `Modal`: starts a capture's upload as soon as it leads the form (I24294). */
  onCaptureReady?: (blob: Blob) => void;
  /** Resolved appearance config, fetched by the controller before this ever mounts. */
  config: WidgetConfig;
  /** Injected screenshot capturer, forwarded to `Modal` — see `Modal.tsx`'s `ModalProps`. */
  capturer: ScreenshotCapturer;
  /** CSS selectors masked before rasterizing a screenshot, forwarded to `Modal`. */
  redactSelectors?: string[];
  /**
   * Injected screen recorder, forwarded to `Modal`. Absent in unit tests and in
   * any browser without `getDisplayMedia`, and the control is not offered then.
   */
  recorder?: ScreenRecorder;
  /**
   * Backend origin, forwarded from the controller so the brand faces can be
   * registered. Null skips that registration: a host that loads the package
   * (Tenger Works) serves its own fonts and cannot reach the API from its
   * renderer (I22658).
   */
  apiBase: string | null;
  /**
   * Forces the light or dark dialog instead of reading the host page's
   * `data-theme`, for a host whose theme names are its own (Works has four
   * dark themes, none called `dark`).
   */
  themeOverride?: Signal<'light' | 'dark' | null>;
  /**
   * `behind-modal` (the default): the modal opens on the click and the capture
   * runs behind it, which needs a capturer that leaves the widget out of the
   * picture, as `SnapdomCapturer` does. `before-open`: capture first, with the
   * whole widget hidden, then open, for a capturer that photographs real
   * pixels, as WebView2's `CapturePreview` in Works does.
   */
  captureMode?: 'behind-modal' | 'before-open';
  /**
   * Bumped by the host to launch the widget as a launcher click would,
   * screenshot included: a keyboard shortcut or a command palette entry.
   */
  launchRequest?: Signal<number>;
  /** Host-defined checkboxes shown in the form, such as Works' app-logs consent. */
  consents?: HostConsent[];
  /** Opens a link the host's own way; see `ModalProps.openLink`. */
  openLink?: (url: string) => void;
}

/**
 * Default request type when the modal opens via the button (rather than an explicit
 * `open(type)` call) — also the controller's fallback for `open()` called with no argument.
 * Exported so `widget-controller.ts` can seed/reset the `requestedType` signal with the same
 * value instead of duplicating it.
 */
export const DEFAULT_REQUEST_TYPE: RequestType = 'BUG';

const NARROW_QUERY = '(max-width: 599.98px)';

/** localStorage key suffix for the button's persisted offset. The edge tab does not use it. */
const DRAG_STORAGE_KEY = 'button';

function readHostTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light';
  const explicit =
    document.documentElement.getAttribute('data-theme') || document.body?.getAttribute('data-theme');
  return explicit === 'dark' ? 'dark' : 'light';
}

/**
 * Root Preact component rendered into the widget's shadow container.
 *
 * `config.launcherStyle` chooses the trigger. `BUTTON` (and any missing or
 * unknown value) is the draggable button. `EDGE_TAB` is the fixed right-edge
 * tab. Both open the same `Modal`.
 *
 * `onOpen` from a launcher captures the page before that modal mounts.
 */
export function App({
  isOpen,
  requestedType,
  onOpen,
  onClose,
  onFiled,
  triggerHidden,
  dockHost,
  onSubmit,
  onCaptureReady,
  config,
  capturer,
  redactSelectors,
  recorder,
  apiBase,
  themeOverride,
  captureMode = 'behind-modal',
  launchRequest,
  consents,
  openLink,
}: AppProps) {
  const launcherStyle = resolveLauncherStyle(config.launcherStyle);
  const isEdge = launcherStyle === 'EDGE_TAB';
  const phoneBarChosen = resolvePhoneLauncher(config.phoneLauncher) === 'BOTTOM_BAR';
  const launcherRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [hostTheme, setTheme] = useState<'light' | 'dark'>(readHostTheme);
  const theme = themeOverride?.value ?? hostTheme;
  const [entering, setEntering] = useState(true);
  const [keyboardUp, setKeyboardUp] = useState(false);
  // True from the launcher click until the screenshot behind the open modal
  // lands. `Modal` shows the turning square in the screenshot slot meanwhile.
  const [openingCapture, setOpeningCapture] = useState(false);
  // Set on the launcher click, before the open state reaches this render, so
  // the button never paints for a frame over the arriving scrim.
  const [launcherConcealed, setLauncherConcealed] = useState(false);
  const [buttonSize, setButtonSize] = useState<{ width: number; height: number } | undefined>(undefined);

  // Always called. Only BUTTON reads `position` and `consumeWasDragged`.
  // A conditional hook would change order when an admin switches style.
  const { position, dragHandlers, consumeWasDragged, anchored } = useDraggable({
    storageKey: DRAG_STORAGE_KEY,
    size: buttonSize,
  });
  const [narrow, setNarrow] = useState(false);

  useLayoutEffect(() => {
    const el = launcherRef.current;
    if (!el || isEdge) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setButtonSize((prev) =>
          prev && prev.width === rect.width && prev.height === rect.height
            ? prev
            : { width: rect.width, height: rect.height },
        );
      }
    };
    measure();
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    return () => observer?.disconnect();
  }, [isEdge]);

  // Registered from App rather than Modal: Modal renders nothing while closed,
  // and the launcher is on screen the whole time.
  useEffect(() => {
    if (apiBase) loadBrandFonts(apiBase);
  }, [apiBase]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const apply = () => setTheme(readHostTheme());
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    if (document.body) {
      observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const tab = launcherRef.current;
    const stop = () => setEntering(false);
    tab?.addEventListener('animationend', stop);
    // The entrance is 400ms delay plus 320ms travel. A timer covers the
    // reduced-motion fade, which still fires `animationend`, and a browser
    // that never starts the animation.
    const id = window.setTimeout(stop, 800);
    return () => {
      tab?.removeEventListener('animationend', stop);
      window.clearTimeout(id);
    };
  }, [dockHost?.value]);

  // Phones: while the on-screen keyboard is up and the modal is closed, the
  // tab fades out so it never sits over one of the host's own fields.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const apply = () => {
      setKeyboardUp(window.innerHeight - vv.height > 120);
    };
    vv.addEventListener('resize', apply);
    apply();
    return () => vv.removeEventListener('resize', apply);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(NARROW_QUERY);
    } catch {
      return;
    }
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  // `--toast-lift` is the corner launcher's height plus 16px, for a host toast
  // stack that chooses to read it (ADR 0032). A dragged button and a docked
  // trigger publish nothing: neither sits in the toast's corner.
  useEffect(() => {
    const publish = () => {
      const launcher = launcherRef.current;
      const corner = Boolean(launcher) && !dockHost?.value && (isEdge || anchored);
      if (!corner || !launcher) {
        clearHostToastLift();
        return;
      }
      const height = launcher.getBoundingClientRect().height;
      if (height > 0) setHostToastLift(`${Math.round(height + 16)}px`);
      else clearHostToastLift();
    };
    publish();
    let mq: MediaQueryList | undefined;
    try {
      mq = window.matchMedia?.(NARROW_QUERY);
    } catch {
      mq = undefined;
    }
    mq?.addEventListener?.('change', publish);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && launcherRef.current) {
      observer = new ResizeObserver(publish);
      observer.observe(launcherRef.current);
    }
    return () => {
      mq?.removeEventListener?.('change', publish);
      observer?.disconnect();
      clearHostToastLift();
    };
  }, [isOpen.value, isEdge, anchored, buttonSize, dockHost?.value]);

  // A modal dialog hands focus back to the control that opened it, however it
  // was closed: Escape, the close button, Done, or a click on the scrim.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !isOpen.value) {
      launcherRef.current?.focus();
    }
    wasOpen.current = isOpen.value;
  }, [isOpen.value]);

  /**
   * Hide this button while the Tenger app-grid panel is open.
   *
   * The two are the only pieces of chrome that float above a Tenger product, and they
   * fight in three ways. This button is appended to <body> so it survives embedding on a
   * customer site; every product's app-grid panel is confined to its own header's stacking
   * context, because a sticky header with a numeric z-index is how every one of them is
   * built. So this button paints OVER that panel and stays clickable. Click it there and two
   * subtler faults follow: <tenger-app-grid> takes Escape in the CAPTURE phase and calls
   * stopPropagation, so it would swallow this modal's Escape, and its focus trap reads its
   * own shadow root's activeElement, which is null while focus sits in THIS closed root, so
   * Shift+Tab would drag focus out of the modal.
   *
   * Hiding the button makes all three unreachable: an unclickable launcher cannot stack a
   * modal over the panel. The reverse ordering is already impossible, because `.ft-overlay`
   * is inset:0 at maximum z-index and covers the header the app-grid trigger sits in.
   *
   * IN THE PACKAGE RATHER THAN IN A HOST, deliberately. Four Tenger products embed this
   * widget and load this bundle from the Feedback Manager API at runtime, so one deploy
   * reaches all of them. A customer embedding this widget without app-grid gets two
   * listeners that never fire.
   *
   * `visibility`, not `display`, and set on the node directly: a collapsed box is not what
   * the tab's fixed geometry is measured from, and a display change would cancel the
   * entrance animation.
   */
  useEffect(() => {
    const setHidden = (hidden: boolean) => {
      const launcher = launcherRef.current;
      if (launcher) launcher.style.visibility = hidden ? 'hidden' : '';
    };
    const onGridOpen = () => setHidden(true);
    const onGridClose = () => setHidden(false);
    document.addEventListener('ag-open', onGridOpen);
    document.addEventListener('ag-close', onGridClose);
    return () => {
      document.removeEventListener('ag-open', onGridOpen);
      document.removeEventListener('ag-close', onGridClose);
      setHidden(false);
    };
  }, []);

  // The screenshot taken on the click that is about to open the modal, handed
  // to `Modal` as its `pendingScreenshot`.
  const [capturedScreenshot, setCapturedScreenshot] = useState<Blob | null>(null);

  // A programmatic `FeedbackTracker.open()` bypasses the launcher and so takes
  // no screenshot. Without this, it would reopen carrying whatever the last
  // launcher click captured — a picture of some other page, from some other
  // moment, silently attached to an unrelated report.
  useEffect(() => {
    if (!isOpen.value) setCapturedScreenshot(null);
  }, [isOpen.value]);

  /**
   * Opens the modal at once, then captures the page behind it (I22657).
   *
   * It used to be the other way round: capture first, with a dark panel on the
   * page while the raster ran, then open. A capture takes long enough on a
   * heavy page that the panel read as stuck. Now the reporter gets the form
   * immediately, with a small turning square where the screenshot will land.
   *
   * The open modal cannot reach the picture, because `SnapdomCapturer`
   * excludes the widget's whole shadow host (`[data-ft-host]`, set in
   * `mount.ts`), scrim and launcher included. A capturer that photographs real
   * pixels could not honor that, which is why the exclusion lives in the
   * capturer and not in a visibility trick here.
   *
   * A capture failure leaves the modal with no screenshot and NO error. This is
   * a silent convenience nobody asked for; failing it must not produce UI. The
   * manual capture button still surfaces its error, because there the user
   * pressed something and is owed an answer.
   */
  const openWithCapture = async () => {
    if (!isEdge && consumeWasDragged()) return;
    if (captureMode === 'before-open') {
      await captureThenOpen();
      return;
    }

    try {
      setLauncherConcealed(true);
      setCapturedScreenshot(null);
      setOpeningCapture(true);
      onOpen();
    } catch {
      // Same boundary as below: nothing may throw into the host page.
      return;
    }

    // Let the modal paint before the raster starts, so the reporter sees it
    // straight away rather than after the capture.
    await new Promise((resolve) => setTimeout(resolve, 0));

    let blob: Blob | null = null;
    try {
      blob = await capturer.capture({ redactSelectors });
    } catch {
      // Never block the form on a screenshot.
    }

    try {
      // Dropped if the reporter closed the modal while the raster ran: a
      // picture delivered to a closed modal would be picked up by the next
      // programmatic `open()`, a screenshot of another moment on another report.
      if (isOpen.value) setCapturedScreenshot(blob);
      setOpeningCapture(false);
    } catch {
      // `openWithCapture`'s promise is never awaited by its caller (see
      // `handleOpen` below) — anything thrown past this point would surface
      // as an unhandled rejection in the HOST page, which the widget must
      // never do. Nothing above is expected to throw, but this is the last
      // line of defense for the guarantee the comment on `openWithCapture`
      // (and `handleOpen`'s `void`) both claim to hold.
    }
  };

  /**
   * Hides the whole widget for one capture and restores it, for a capturer
   * that photographs real pixels and so cannot be told to leave the widget out.
   * `visibility`, not `display`, so nothing reflows under the raster. Reached
   * through `getRootNode()`: the root is a CLOSED shadow root, so its `.host`
   * is the only handle to that element anything inside the tree can get.
   */
  const captureWithWidgetHidden = async (): Promise<Blob | null> => {
    const root = rootRef.current?.getRootNode();
    const host = root instanceof ShadowRoot ? (root.host as HTMLElement) : null;
    const previous = host?.style.visibility ?? '';
    if (host) host.style.visibility = 'hidden';
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      return await capturer.capture({ redactSelectors });
    } catch {
      return null;
    } finally {
      if (host) host.style.visibility = previous;
    }
  };

  /** The `before-open` order (I22658): capture with the widget hidden, then open with the picture. */
  const captureThenOpen = async () => {
    setLauncherConcealed(true);
    const blob = await captureWithWidgetHidden();
    try {
      setCapturedScreenshot(blob);
      onOpen();
    } catch {
      // Same boundary as `openWithCapture`: nothing may throw into the host page.
    }
  };

  // The DOM handler gets a plain function, never the promise — an unhandled
  // rejection escaping here would cross the widget's never-throw-into-the-host
  // boundary. `openWithCapture` swallows its own failures; `void` documents
  // that the promise is intentionally unobserved.
  const handleOpen = () => {
    void openWithCapture();
  };

  const toggle = () => {
    if (isOpen.value) onClose();
    else handleOpen();
  };

  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;

  // Option B: a host with a masthead slot docks the trigger and the edge tab
  // stays off the page. Phones default to the edge tab (option A) until a host
  // calls `mountTrigger`.
  const docked = isEdge ? (dockHost?.value ?? null) : null;

  // A host with no API origin (Works) hands over the mask itself, as a data
  // URL its renderer may load; otherwise the path is resolved against `apiBase`.
  const resolvedShapeAssetUrl =
    config.shapeAssetUrl ??
    (config.shapeAssetPath && apiBase ? `${apiBase.replace(/\/+$/, '')}${config.shapeAssetPath}` : null);

  // The application's Appearance, resolved for the edge tab, the phone dock and
  // a docked trigger (I22656). The shape asset path is resolved against
  // `apiBase` here for the same reason the button's is below.
  const tabAppearance = resolveEdgeTabAppearance({
    ...config,
    shapeAssetUrl: resolvedShapeAssetUrl,
  });
  const dockFill = tabAppearance.fill ?? '#041c74';
  const dockInk = tabAppearance.ink ?? '#ffffff';
  const dockLabel = tabAppearance.label;
  const dockIconUrl = config.iconUrl ?? null;
  useEffect(() => {
    if (!docked) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ft-dock';
    button.setAttribute('aria-controls', EDGE_PANEL_ID);
    button.style.width = '44px';
    button.style.height = '44px';
    button.style.display = 'inline-flex';
    button.style.alignItems = 'center';
    button.style.justifyContent = 'center';
    button.style.padding = '0';
    button.style.border = '0';
    // The application's own colors and icon (I22656). Inline, because this
    // button is in the host's light DOM and no widget stylesheet reaches it.
    button.style.background = dockFill;
    button.style.color = dockInk;
    button.style.cursor = 'pointer';
    // The button lives in the host's light DOM, so the shadow `.ft-dock`
    // rule never reaches it, and a <style> in document.head is forbidden.
    // Transparent, not none: the ring is relocated, not suppressed (ADR 0006).
    // The 2px offset is the Catalina gap between the control and the Sky ring.
    button.style.outline = '2px solid transparent';
    button.style.outlineOffset = '2px';
    if (tabAppearance.icon) {
      renderInto(tabAppearance.icon, button);
    } else {
      button.innerHTML = DOCK_ICON_MARKUP;
    }
    const sync = () => {
      const open = isOpen.value;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      button.setAttribute('aria-label', dockLabel);
      button.style.visibility = triggerHidden?.value ? 'hidden' : '';
    };
    const onClick = () => toggleRef.current();
    const onFocus = () => {
      if (button.matches(':focus-visible')) button.style.outlineColor = '#1d94ff';
    };
    const onBlur = () => {
      button.style.outlineColor = 'transparent';
    };
    button.addEventListener('click', onClick);
    button.addEventListener('focus', onFocus);
    button.addEventListener('blur', onBlur);
    docked.appendChild(button);
    launcherRef.current = button;
    sync();
    const unsubOpen = isOpen.subscribe(sync);
    const unsubHidden = triggerHidden?.subscribe(sync);
    return () => {
      unsubOpen();
      unsubHidden?.();
      button.removeEventListener('click', onClick);
      button.removeEventListener('focus', onFocus);
      button.removeEventListener('blur', onBlur);
      renderInto(null, button);
      button.remove();
      if (launcherRef.current === button) launcherRef.current = null;
    };
    // `tabAppearance` is rebuilt every render; its inputs are what matter here.
  }, [docked, isOpen, triggerHidden, dockFill, dockInk, dockLabel, dockIconUrl]);

  /**
   * Retakes the page while the modal stays on screen.
   *
   * This used to hide the whole shadow host for the raster, so the dialog
   * vanished for as long as the capture took. The capturer now leaves the
   * widget's host out of the picture by itself (see `openWithCapture`), so the
   * reporter keeps the form, and the Retake control says it is retaking.
   *
   * Resolves `null` on failure rather than throwing, so `Modal` can keep the
   * capture the reporter already had.
   */
  const retake = async (): Promise<Blob | null> => {
    // A pixel capturer would photograph the open modal, so that mode hides the
    // widget for the retake, as every retake used to.
    if (captureMode === 'before-open') return captureWithWidgetHidden();
    try {
      return await capturer.capture({ redactSelectors });
    } catch {
      return null;
    }
  };

  // A host's own way in (a shortcut, a palette entry) launches exactly as the
  // launcher does, screenshot included. Skips the value it mounted with.
  const lastLaunch = useRef(launchRequest?.value ?? 0);
  useEffect(() => {
    const value = launchRequest?.value ?? 0;
    if (value === lastLaunch.current) return;
    lastLaunch.current = value;
    if (!isOpen.value) handleOpen();
  }, [launchRequest?.value]);

  const buttonConfig = { ...config, shapeAssetUrl: resolvedShapeAssetUrl };

  useEffect(() => {
    if (!isOpen.value) setLauncherConcealed(false);
  }, [isOpen.value]);

  const suppressed = triggerHidden?.value ?? false;
  // Hidden while the modal is open, as the button is. Its Close state belonged
  // to the drawer, where the tab sat beside the panel as its handle; under the
  // modal's scrim a dimmed Close reads as a second, unreachable control.
  const tabHidden = isOpen.value || keyboardUp;
  // Option B is an admin choice, not a host call. On a phone it replaces the
  // small tab with a 44px trigger just above the host's bottom bar. A wide
  // screen keeps the tab. `mountTrigger` still wins: a host slot is a placed
  // control, and a second trigger beside it would be two ways to open the same form.
  const phoneBar = isEdge && !docked && narrow && phoneBarChosen;

  return (
    <div
      ref={rootRef}
      class={suppressed ? 'ft-root is-suppressed' : 'ft-root'}
      data-theme={theme}
      style={tabAppearance.vars}
    >
      {isEdge && !docked && !phoneBar ? (
        <EdgeTab
          ref={launcherRef}
          open={isOpen.value}
          hidden={tabHidden}
          entering={entering}
          onToggle={toggle}
          label={tabAppearance.label}
          tab={tabAppearance.tab}
          icon={tabAppearance.icon}
          tooltip={tabAppearance.tooltip}
        />
      ) : null}
      {phoneBar ? (
        <button
          ref={launcherRef}
          type="button"
          class={isOpen.value ? 'ft-phone-dock is-hidden' : 'ft-phone-dock'}
          aria-expanded={isOpen.value ? 'true' : 'false'}
          aria-controls={EDGE_PANEL_ID}
          aria-label={tabAppearance.label}
          onClick={toggle}
        >
          <span class="ft-tab__icon">{tabAppearance.icon ?? <DockIcon />}</span>
        </button>
      ) : null}
      {!isEdge ? (
        <Button
          ref={launcherRef}
          class="ft-launcher"
          config={buttonConfig}
          onOpen={handleOpen}
          concealed={isOpen.value || launcherConcealed}
          style={{
            position: 'fixed',
            zIndex: 2147483647,
            // Below 600px the default corner is `bottom`, so the host's nav
            // and the toast lift are in the offset. A dragged button keeps
            // the left/top the pointer left it at.
            ...(narrow && anchored
              ? {
                  right: '12px',
                  bottom:
                    'calc(env(safe-area-inset-bottom) + var(--fb-nav-h, 0px) + var(--toast-lift, 0px) + 12px)',
                  left: 'auto',
                  top: 'auto',
                }
              : {
                  left: `${position.x}px`,
                  top: `${position.y}px`,
                }),
            // Hidden while the modal is open. The modal's close control is the
            // way back, and a button left visible under the scrim would still
            // show its tooltip on hover.
            visibility: launcherConcealed || isOpen.value ? 'hidden' : undefined,
          }}
          {...dragHandlers}
        />
      ) : null}
      {/*
        One Modal for every launcher. `launcherStyle` chooses the trigger and
        nothing else (I22642): #137 let the edge tab's drawer form ride along
        to every application, and `App.test.tsx` now proves both styles open
        the identical dialog.
      */}
      <Modal
        open={isOpen.value}
        initialType={requestedType.value}
        onClose={onClose}
        onFiled={onFiled}
        clientPortalUrl={config.clientPortalUrl}
        onSubmit={onSubmit}
        onCaptureReady={onCaptureReady}
        capturer={capturer}
        redactSelectors={redactSelectors}
        config={config}
        pendingScreenshot={capturedScreenshot}
        capturePending={openingCapture}
        consents={consents}
        openLink={openLink}
        onRetake={retake}
        recorder={recorder}
      />
      <style>{edgeTabStyles}</style>
    </div>
  );
}
